/**
 * ConfirmDialog — accessible "are you sure?" modal.
 *
 * Used for destructive-feeling admin actions (archive a learner, etc.).
 * Focus-traps the modal (Tab + Shift+Tab cycle inside the dialog),
 * restores focus to the trigger on close, ESC dismisses, and the
 * backdrop click is treated as cancel. The dialog is intentionally
 * minimal — no portal, no animation — to keep the Vitest + JSDOM
 * unit-test surface stable.
 *
 * Use ``confirmTone="danger"`` to color the confirm button rose; default
 * is the primary brand color (used when the action is reversible).
 */
import type * as React from "react";
import { useEffect, useRef } from "react";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /** "danger" → rose confirm button; default → primary. */
  confirmTone?: "primary" | "danger";
  /** Disables both buttons (e.g. while the action is in-flight). */
  busy?: boolean;
  /**
   * Disable just the confirm button (Cancel stays active). Used for typed-
   * confirm guards on irreversible actions — the parent controls when the
   * user has typed enough to enable the action.
   */
  confirmDisabled?: boolean;
}

/**
 * Collect every focusable descendant of ``root`` in DOM order. Skips
 * disabled and aria-hidden subtrees. Matches the set most focus-trap
 * libraries use; good enough for our two-button dialog + future
 * variants.
 */
function getFocusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute("inert") && el.getAttribute("aria-hidden") !== "true",
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  confirmTone = "primary",
  busy = false,
  confirmDisabled = false,
}: ConfirmDialogProps): React.JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Focus the confirm button on open + restore focus on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null;
    confirmRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Focus trap + ESC handler. Attached at the document level so it
  // catches the Tab/Shift+Tab keystroke even if focus has slipped to
  // a background button (which is exactly the case the trap exists to
  // recover from).
  useEffect(() => {
    if (!open) return;

    function handleKeydown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        if (busy) return;
        e.preventDefault();
        e.stopPropagation();
        onCancel();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusables(dialogRef.current);
      if (focusables.length === 0) {
        // Nothing focusable inside — pin focus to the dialog itself.
        e.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      const insideDialog =
        active !== null && dialogRef.current?.contains(active) === true;

      if (e.shiftKey) {
        // Shift+Tab: from first → wrap to last. Also catches the case
        // where focus has escaped the dialog entirely (e.g. via a
        // screen-reader skip-link); bounce it back to the last
        // focusable inside.
        if (!insideDialog || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: from last → wrap to first. Same escape recovery as
        // above for the forward direction.
        if (!insideDialog || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeydown, true);
    return () => {
      document.removeEventListener("keydown", handleKeydown, true);
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClass =
    confirmTone === "danger"
      ? "rounded-md border border-rose-700 bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-60"
      : "rounded-md border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60";

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop. Click cancels. Label is distinct from the visible
          Cancel button so accessible-name queries don't see two
          "Cancel" controls. */}
      <button
        type="button"
        aria-label="Dismiss dialog"
        tabIndex={-1}
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-desc"
        tabIndex={-1}
        className="relative z-10 w-full max-w-md rounded-lg border border-border bg-white p-6 shadow-xl"
      >
        <h2
          id="confirm-dialog-title"
          className="text-lg font-semibold text-foreground"
        >
          {title}
        </h2>
        <div
          id="confirm-dialog-desc"
          className="mt-2 text-sm text-muted-foreground"
        >
          {description}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            onClick={() => {
              void onConfirm();
            }}
            disabled={busy || confirmDisabled}
            className={confirmClass}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
